import type { CorpusNode } from '../stores/corpus'
import type { TiptapDocJSON } from '../utils/contentUtils'

// Blog publishing keeps front matter out of the manuscript. A desk or folder
// carries a publishing profile (which static site generator, which front
// matter format, which fields), and each page inside it stores only the values
// the writer actually chose. Everything else is derived at export time.

export type FrontmatterFormat = 'yaml' | 'toml' | 'json'
export type FrontmatterFieldType = 'text' | 'date' | 'boolean' | 'list' | 'number'
/**
 * Where a field's value comes from when the writer has not set one.
 * - title, slug, modifiedDate: always follow the page (live).
 * - publishDate: fixed to the day of the first export, then stored.
 * - excerpt: a suggestion from the first paragraph the writer confirms.
 */
export type FrontmatterSource = 'none' | 'title' | 'slug' | 'publishDate' | 'modifiedDate' | 'excerpt'
export type FrontmatterValue = string | number | boolean | string[]
export type FrontmatterValues = Record<string, FrontmatterValue>
export type PublishingPresetId = 'hugo' | 'jekyll' | 'astro' | 'eleventy' | 'zola' | 'custom'

export interface FrontmatterField {
  /** Front matter key. Dots create nested tables, e.g. `taxonomies.tags`. */
  key: string
  type: FrontmatterFieldType
  source: FrontmatterSource
  required: boolean
  defaultValue?: FrontmatterValue
}

export interface PublishingProfile {
  preset: PublishingPresetId
  format: FrontmatterFormat
  /** Suggested export file name. Tokens: {slug}, {date}, {title}. */
  fileName: string
  fields: FrontmatterField[]
}

export const FRONTMATTER_FORMATS: { id: FrontmatterFormat; label: string }[] = [
  { id: 'yaml', label: 'YAML (---)' },
  { id: 'toml', label: 'TOML (+++)' },
  { id: 'json', label: 'JSON ({ })' },
]

export const FIELD_TYPES: { id: FrontmatterFieldType; label: string }[] = [
  { id: 'text', label: 'Text' },
  { id: 'date', label: 'Date' },
  { id: 'list', label: 'List' },
  { id: 'boolean', label: 'Yes / no' },
  { id: 'number', label: 'Number' },
]

export const FIELD_SOURCES: { id: FrontmatterSource; label: string; types: FrontmatterFieldType[] }[] = [
  { id: 'none', label: 'Ask me', types: ['text', 'date', 'list', 'boolean', 'number'] },
  { id: 'title', label: 'Page title', types: ['text'] },
  { id: 'slug', label: 'Slug from title', types: ['text'] },
  { id: 'excerpt', label: 'First paragraph', types: ['text'] },
  { id: 'publishDate', label: 'First export date', types: ['date', 'text'] },
  { id: 'modifiedDate', label: 'Export date', types: ['date', 'text'] },
]

const LIVE_SOURCES: FrontmatterSource[] = ['title', 'slug', 'modifiedDate']

function field(key: string, type: FrontmatterFieldType, source: FrontmatterSource = 'none', required = false, defaultValue?: FrontmatterValue): FrontmatterField {
  return defaultValue === undefined ? { key, type, source, required } : { key, type, source, required, defaultValue }
}

interface PublishingPreset {
  id: PublishingPresetId
  label: string
  description: string
  profile: () => Omit<PublishingProfile, 'preset'>
}

export const PUBLISHING_PRESETS: PublishingPreset[] = [
  {
    id: 'hugo',
    label: 'Hugo',
    description: 'Title, date, draft, description, tags, and categories.',
    profile: () => ({
      format: 'yaml',
      fileName: '{slug}.md',
      fields: [
        field('title', 'text', 'title', true),
        field('date', 'date', 'publishDate', true),
        field('draft', 'boolean', 'none', false, false),
        field('description', 'text', 'excerpt'),
        field('tags', 'list'),
        field('categories', 'list'),
      ],
    }),
  },
  {
    id: 'jekyll',
    label: 'Jekyll',
    description: 'Posts named by date, with layout, categories, and tags.',
    profile: () => ({
      format: 'yaml',
      fileName: '{date}-{slug}.md',
      fields: [
        field('layout', 'text', 'none', false, 'post'),
        field('title', 'text', 'title', true),
        field('date', 'date', 'publishDate', true),
        field('categories', 'list'),
        field('tags', 'list'),
      ],
    }),
  },
  {
    id: 'astro',
    label: 'Astro',
    description: 'The blog starter schema: title, description, and pubDate.',
    profile: () => ({
      format: 'yaml',
      fileName: '{slug}.md',
      fields: [
        field('title', 'text', 'title', true),
        field('description', 'text', 'excerpt', true),
        field('pubDate', 'date', 'publishDate', true),
        field('updatedDate', 'date', 'modifiedDate'),
        field('tags', 'list'),
      ],
    }),
  },
  {
    id: 'eleventy',
    label: 'Eleventy',
    description: 'Title, description, date, and collection tags.',
    profile: () => ({
      format: 'yaml',
      fileName: '{slug}.md',
      fields: [
        field('title', 'text', 'title', true),
        field('description', 'text', 'excerpt'),
        field('date', 'date', 'publishDate', true),
        field('tags', 'list', 'none', false, ['posts']),
      ],
    }),
  },
  {
    id: 'zola',
    label: 'Zola',
    description: 'TOML front matter with tags under taxonomies.',
    profile: () => ({
      format: 'toml',
      fileName: '{slug}.md',
      fields: [
        field('title', 'text', 'title', true),
        field('date', 'date', 'publishDate', true),
        field('description', 'text', 'excerpt'),
        field('draft', 'boolean', 'none', false, false),
        field('taxonomies.tags', 'list'),
      ],
    }),
  },
  {
    id: 'custom',
    label: 'Custom',
    description: 'Start from a title and date and add your own fields.',
    profile: () => ({
      format: 'yaml',
      fileName: '{slug}.md',
      fields: [
        field('title', 'text', 'title', true),
        field('date', 'date', 'publishDate', true),
      ],
    }),
  },
]

export function presetLabel(id: PublishingPresetId): string {
  return PUBLISHING_PRESETS.find(preset => preset.id === id)?.label || 'Custom'
}

export function createPublishingProfile(preset: PublishingPresetId): PublishingProfile {
  const source = PUBLISHING_PRESETS.find(item => item.id === preset) || PUBLISHING_PRESETS[PUBLISHING_PRESETS.length - 1]
  return { preset: source.id, ...source.profile() }
}

// ── Validation (profiles and values arrive from synced, user-editable data) ──

const MAX_FIELDS = 40
const MAX_TEXT = 2000

function isFormat(value: unknown): value is FrontmatterFormat {
  return FRONTMATTER_FORMATS.some(format => format.id === value)
}

function isFieldType(value: unknown): value is FrontmatterFieldType {
  return FIELD_TYPES.some(type => type.id === value)
}

function isSource(value: unknown): value is FrontmatterSource {
  return FIELD_SOURCES.some(source => source.id === value)
}

export function isValidFieldKey(key: string): boolean {
  return /^[^.\s]+(\.[^.\s]+)*$/.test(key) && key.length <= 80
}

/** Coerces a stored value to the field's type, or undefined when it cannot be. */
export function coerceFieldValue(type: FrontmatterFieldType, value: unknown): FrontmatterValue | undefined {
  if (value === undefined || value === null) return undefined
  if (type === 'boolean') return typeof value === 'boolean' ? value : value === 'true'
  if (type === 'number') {
    const number = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(number) ? number : undefined
  }
  if (type === 'list') {
    const items = Array.isArray(value) ? value : typeof value === 'string' ? value.split(',') : []
    return items.filter((item): item is string => typeof item === 'string').map(item => item.trim().slice(0, 200)).filter(Boolean).slice(0, 100)
  }
  return typeof value === 'string' ? value.slice(0, MAX_TEXT) : String(value).slice(0, MAX_TEXT)
}

export function normalizePublishingProfile(raw: any): PublishingProfile | undefined {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.fields)) return undefined
  const preset: PublishingPresetId = PUBLISHING_PRESETS.some(item => item.id === raw.preset) ? raw.preset : 'custom'
  const seen = new Set<string>()
  const fields: FrontmatterField[] = []
  for (const item of raw.fields.slice(0, MAX_FIELDS)) {
    const key = typeof item?.key === 'string' ? item.key.trim() : ''
    if (!isValidFieldKey(key) || seen.has(key)) continue
    seen.add(key)
    const type = isFieldType(item.type) ? item.type : 'text'
    const source = isSource(item.source) && FIELD_SOURCES.find(s => s.id === item.source)!.types.includes(type) ? item.source : 'none'
    const defaultValue = coerceFieldValue(type, item.defaultValue)
    fields.push(field(key, type, source, item.required === true, defaultValue))
  }
  return {
    preset,
    format: isFormat(raw.format) ? raw.format : 'yaml',
    fileName: typeof raw.fileName === 'string' && raw.fileName.trim() ? raw.fileName.trim().slice(0, 200) : '{slug}.md',
    fields,
  }
}

export function normalizeFrontmatterValues(raw: any): FrontmatterValues | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const values: FrontmatterValues = {}
  for (const [key, value] of Object.entries(raw).slice(0, MAX_FIELDS * 2)) {
    if (!isValidFieldKey(key)) continue
    if (typeof value === 'string') values[key] = value.slice(0, MAX_TEXT)
    else if (typeof value === 'number' && Number.isFinite(value)) values[key] = value
    else if (typeof value === 'boolean') values[key] = value
    else if (Array.isArray(value)) values[key] = coerceFieldValue('list', value) as string[]
  }
  return values
}

// ── Resolution ──────────────────────────────────────────────────────────────

/** The nearest desk or folder above the page that has a publishing profile. */
export function findPublishingContainer(path: CorpusNode[]): CorpusNode | null {
  for (let index = path.length - 2; index >= 0; index--) {
    const node = path[index]
    if (node.subtype !== 'page' && node.publishing) return node
  }
  return null
}

export function slugify(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
    .replace(/-+$/g, '') || 'post'
}

export function localDate(date = new Date()): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function nodeText(node: any): string {
  if (node?.type === 'text') return node.text || ''
  if (node?.type === 'hardBreak') return ' '
  return Array.isArray(node?.content) ? node.content.map(nodeText).join('') : ''
}

/** The first non-empty paragraph, trimmed to roughly one search-snippet length. */
export function extractExcerpt(doc: TiptapDocJSON | null | undefined, limit = 200): string {
  for (const node of doc?.content || []) {
    if (node?.type !== 'paragraph') continue
    const text = nodeText(node).replace(/\s+/g, ' ').trim()
    if (!text) continue
    if (text.length <= limit) return text
    const cut = text.slice(0, limit)
    const lastSpace = cut.lastIndexOf(' ')
    return `${(lastSpace > limit * 0.6 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.–—-]+$/, '')}…`
  }
  return ''
}

export interface PostContext {
  title: string
  doc: TiptapDocJSON | null
  now?: Date
}

/** What a field would be if the writer never touched it. */
export function derivedValue(field: FrontmatterField, context: PostContext): FrontmatterValue | undefined {
  switch (field.source) {
    case 'title': return context.title
    case 'slug': return slugify(context.title)
    case 'publishDate':
    case 'modifiedDate': return localDate(context.now)
    case 'excerpt': return extractExcerpt(context.doc) || undefined
    default: return field.defaultValue
  }
}

export function isEmptyValue(value: FrontmatterValue | undefined): boolean {
  return value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
}

function sameValue(a: FrontmatterValue | undefined, b: FrontmatterValue | undefined): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
}

export interface ResolvedPost {
  /** Values in profile order, as they will be written. */
  values: FrontmatterValues
  /** Required fields that still have no value. */
  missing: string[]
  /** True until the writer has confirmed this page's details once. */
  needsReview: boolean
}

export function resolvePost(profile: PublishingProfile, stored: FrontmatterValues | undefined, context: PostContext): ResolvedPost {
  const values: FrontmatterValues = {}
  const missing: string[] = []
  for (const item of profile.fields) {
    const own = stored ? coerceFieldValue(item.type, stored[item.key]) : undefined
    const value = own !== undefined ? own : derivedValue(item, context)
    if (value !== undefined) values[item.key] = value
    if (item.required && isEmptyValue(value)) missing.push(item.key)
  }
  return { values, missing, needsReview: stored === undefined }
}

/**
 * Reduces confirmed values to what must be remembered: anything that differs
 * from what the profile would derive, plus the accepted excerpt and, once the
 * post is exported, its publish date, which would otherwise drift over time.
 */
export function valuesToStore(profile: PublishingProfile, values: FrontmatterValues, context: PostContext, previous: FrontmatterValues | undefined, exporting: boolean): FrontmatterValues {
  const stored: FrontmatterValues = {}
  // Keep values for fields that are no longer in the profile so switching
  // profiles back and forth does not lose them.
  for (const [key, value] of Object.entries(previous || {})) {
    if (!profile.fields.some(item => item.key === key)) stored[key] = value
  }
  for (const item of profile.fields) {
    const value = coerceFieldValue(item.type, values[item.key])
    if (value === undefined) continue
    const keepAlways = item.source === 'excerpt' || (item.source === 'publishDate' && exporting)
    if (keepAlways ? !isEmptyValue(value) : !sameValue(value, derivedValue(item, context))) stored[item.key] = value
  }
  return stored
}

export function isLiveSource(source: FrontmatterSource): boolean {
  return LIVE_SOURCES.includes(source)
}

// ── Serialization ───────────────────────────────────────────────────────────

type Tree = { [key: string]: FrontmatterValue | Tree }

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:\d{2})?)?$/

function buildTree(profile: PublishingProfile, values: FrontmatterValues): { tree: Tree; types: Map<string, FrontmatterFieldType> } {
  const tree: Tree = {}
  const types = new Map<string, FrontmatterFieldType>()
  for (const item of profile.fields) {
    const value = values[item.key]
    if (isEmptyValue(value)) continue
    const parts = item.key.split('.')
    let branch = tree
    let blocked = false
    for (const part of parts.slice(0, -1)) {
      const next = branch[part]
      if (next === undefined) branch[part] = {}
      else if (typeof next !== 'object' || Array.isArray(next)) { blocked = true; break }
      branch = branch[part] as Tree
    }
    const leaf = parts[parts.length - 1]
    if (blocked || (leaf in branch && typeof branch[leaf] === 'object' && !Array.isArray(branch[leaf]))) continue
    branch[leaf] = value
    types.set(item.key, item.type)
  }
  return { tree, types }
}

function isTable(value: FrontmatterValue | Tree): value is Tree {
  return typeof value === 'object' && !Array.isArray(value)
}

function quote(text: string): string {
  // JSON strings are valid YAML and TOML basic strings, apart from DEL, which
  // TOML requires to be escaped.
  return JSON.stringify(text).replace(/\u007f/g, '\\u007F')
}

function scalar(value: FrontmatterValue, type: FrontmatterFieldType | undefined): string {
  if (typeof value === 'boolean' || typeof value === 'number') return String(value)
  if (Array.isArray(value)) return `[${value.map(quote).join(', ')}]`
  if (type === 'date' && DATE_PATTERN.test(value)) return value
  return quote(value)
}

function yamlKey(key: string): string {
  return /^[A-Za-z_][\w-]*$/.test(key) ? key : quote(key)
}

function tomlKey(key: string): string {
  return /^[A-Za-z0-9_-]+$/.test(key) ? key : quote(key)
}

function toYaml(tree: Tree, types: Map<string, FrontmatterFieldType>, path: string[] = [], indent = ''): string[] {
  const lines: string[] = []
  for (const [key, value] of Object.entries(tree)) {
    const fullKey = [...path, key].join('.')
    if (isTable(value)) {
      lines.push(`${indent}${yamlKey(key)}:`, ...toYaml(value, types, [...path, key], `${indent}  `))
    } else if (Array.isArray(value) && value.length) {
      lines.push(`${indent}${yamlKey(key)}:`, ...value.map(item => `${indent}  - ${quote(item)}`))
    } else {
      lines.push(`${indent}${yamlKey(key)}: ${scalar(value, types.get(fullKey))}`)
    }
  }
  return lines
}

function toToml(tree: Tree, types: Map<string, FrontmatterFieldType>, path: string[] = []): string[] {
  const lines: string[] = []
  const tables: [string, Tree][] = []
  for (const [key, value] of Object.entries(tree)) {
    if (isTable(value)) tables.push([key, value])
    else lines.push(`${tomlKey(key)} = ${scalar(value, types.get([...path, key].join('.')))}`)
  }
  for (const [key, table] of tables) {
    const tablePath = [...path, key]
    lines.push('', `[${tablePath.map(tomlKey).join('.')}]`, ...toToml(table, types, tablePath))
  }
  return lines
}

export function serializeFrontmatter(profile: PublishingProfile, values: FrontmatterValues): string {
  const { tree, types } = buildTree(profile, values)
  if (profile.format === 'json') return `${JSON.stringify(tree, null, 2)}\n`
  if (profile.format === 'toml') return `+++\n${toToml(tree, types).join('\n')}\n+++\n`
  return `---\n${toYaml(tree, types).join('\n')}\n---\n`
}

export function composePost(profile: PublishingProfile, values: FrontmatterValues, markdown: string): string {
  return `${serializeFrontmatter(profile, values)}\n${markdown.replace(/^\s+/, '')}`
}

/**
 * A safe relative path inside a publish folder: forward slashes, no empty,
 * `.` or `..` segments, and a Markdown extension.
 */
export function normalizeRelativePostPath(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined
  const segments = raw.replace(/\\/g, '/').split('/')
    .map(segment => segment.replace(/[<>:"|?*\u0000-\u001f]/g, '-').trim().replace(/[. ]+$/g, ''))
  if (segments.some(segment => !segment || segment === '.' || segment === '..')) return undefined
  const path = segments.join('/')
  return path.length <= 300 && /\.(md|markdown|mdx)$/i.test(path) ? path : undefined
}

/** The post's path relative to the publish folder. May contain folders. */
export function postFilePath(profile: PublishingProfile, values: FrontmatterValues, context: PostContext): string {
  const dateField = profile.fields.find(item => item.source === 'publishDate')
  const rawDate = dateField ? values[dateField.key] : undefined
  const date = typeof rawDate === 'string' && DATE_PATTERN.test(rawDate) ? rawDate.slice(0, 10) : localDate(context.now)
  const slugField = profile.fields.find(item => item.source === 'slug')
  const slugValue = slugField ? values[slugField.key] : undefined
  const slug = typeof slugValue === 'string' && slugValue.trim() ? slugify(slugValue) : slugify(context.title)
  const name = profile.fileName
    .replace(/\{slug\}/g, slug)
    .replace(/\{date\}/g, date)
    .replace(/\{title\}/g, context.title.replace(/[/\\]/g, '-'))
    .trim()
  return normalizeRelativePostPath(/\.(md|markdown|mdx)$/i.test(name) ? name : `${name}.md`) || `${slug}.md`
}

/**
 * A file name for the save dialog. Page bundles like `{slug}/index.md` are
 * named after their folder, since a bare `index.md` says nothing.
 */
export function postFileName(profile: PublishingProfile, values: FrontmatterValues, context: PostContext): string {
  const segments = postFilePath(profile, values, context).split('/')
  const last = segments[segments.length - 1]
  if (segments.length > 1 && /^_?index\.(md|markdown|mdx)$/i.test(last)) return `${segments[segments.length - 2]}.md`
  return last
}
